import { useCallback } from 'react';
import { useAccount, useWalletClient, usePublicClient } from 'wagmi';
import { erc20Abi, formatUnits, parseUnits } from 'viem';
import {
    contractData,
    tradeAddress,
    usdtAddress
} from '@/react-app/components/web3';
import { usdtApproveAbi } from '@/react-app/components/web3';
import Swal from 'sweetalert2';
import { notifications } from '@mantine/notifications';



type Params = {
    amount: number,
    userId: string,
    plan: string,
    planId: string

}


export const useBlockPayment = () => {
    const { address, isConnected } = useAccount();
    const { data: walletClient } = useWalletClient();
    const publicClient = usePublicClient();


    return useCallback(
        async (params: Params) => {

            const investEntrance = {
                amount: parseUnits(params.amount.toString(), 18),
                userId: params.userId,
                plan: params.plan,
                planId: params.planId

            }


            if (!address || !publicClient) return;
            const rawBalance = await publicClient?.readContract({
                address: usdtAddress,
                abi: erc20Abi,
                functionName: 'balanceOf',
                args: [address],
            });


            const formattedBalance = parseFloat(formatUnits(rawBalance as bigint, 18)).toFixed(3);

            if (!isConnected || !walletClient) {
                Swal.fire({
                    text: 'Wallet not connected or provider not available',
                    icon: 'error',
                    confirmButtonColor: '#ff7849',
                })
                return;
            }

            if (Number(params.amount) > Number(formattedBalance)) {

                Swal.fire({
                    text: `Input amount is more than your USDT balance of ${formattedBalance}`,
                    icon: 'error',
                    confirmButtonColor: '#ff7849',
                })
                return;
            }


            const { enrichAbi } = await contractData();


            try {


                const approveHash = await walletClient.writeContract({
                    address: usdtAddress as `0x${string}`,
                    abi: usdtApproveAbi,
                    functionName: 'approve',
                    args: [tradeAddress, parseUnits(params.amount.toString(), 18)]
                });

                notifications.show({
                    message: 'Approval transaction sent. Waiting for confirmation...',
                });

                const approveReceipt = await publicClient?.waitForTransactionReceipt({
                    hash: approveHash,
                });

                if (approveReceipt?.status !== 'success') {
                    console.error('Approval failed');
                    notifications.show({
                        message: 'Approval transaction failed.',
                    });
                    return false;
                }

                notifications.show({
                    message: 'Approval confirmed. Sending invest transaction...',
                });



                //Invest
                const investHash = await walletClient.writeContract({
                    address: tradeAddress as `0x${string}`,
                    abi: enrichAbi,
                    functionName: 'invest',
                    args: [
                        investEntrance,
                        usdtAddress,
                    ],
                });

                const investReceipt = await publicClient?.waitForTransactionReceipt({
                    hash: investHash,
                });

                if (investReceipt?.status === 'success') {
                    notifications.show({
                        message: 'Investment successful!',
                    });
                    return true;
                } else {
                    console.error('Investment transaction failed');
                    notifications.show({
                        message: 'Investment transaction failed',
                    });
                    return false;
                }
            } catch (error: unknown) {
                let errorMessage = 'An unexpected error occurred';

                if (typeof error === 'object' && error !== null) {
                    errorMessage = (error as any).message || errorMessage;
                    console.error('Error:', errorMessage);
                } else {
                    console.error(error);
                }


                return errorMessage;
            }
        },
        [isConnected, walletClient, address, publicClient]
    );
};